/**
 * 아이콘 시스템
 *
 * SVG/이미지 에셋을 Icons 맵으로 관리하고,
 * 다크모드 자동 색상 반전 및 커스텀 색상 필터를 지원하는
 * Icon 컴포넌트를 제공한다.
 */

import React from 'react';
import { styled, useTheme as useStyledTheme } from 'styled-components';

// ─────────────────────────────────────────────────────────────
// 에셋 import
// ─────────────────────────────────────────────────────────────

import cartIcon from '@assets/icon/cart.svg';
import cartIconWhite from '@assets/icon/cart_white.svg';
import cartEmpty from '@assets/icon/cart_empty.svg';
import userIcon from '@assets/icon/user.svg';
import copyIcon from '@assets/icon/tabler_copy.svg';
import checkIcon from '@assets/icon/check.svg';
import checkIconRounded from '@assets/icon/check_rounded.svg';
import errorIconRounded from '@assets/icon/error_rounded.svg';
import closeIcon from '@assets/icon/close.svg';
import searchIcon from '@assets/icon/search.svg';
import searchIcon2 from '@assets/icon/search2.svg';
import google from '@assets/google.png';
import kakao from '@assets/kakao.png';
import hidePassword from '@assets/icon/eye_off.svg';
import showPassword from '@assets/icon/eye_on.svg';
import arrowRight from '@assets/icon/arrow-right.svg';
import arrowRight12 from '@assets/icon/arrow_right_12.svg';
import arrowLeft from '@assets/icon/arrow_left.svg';
import arrowLeft2 from '@assets/icon/arrow_left_2.svg';
import arrowLeftWhite from '@assets/icon/arrow_left_white.svg';
import arrowDown from '@assets/icon/arrow_down.svg';
import filter from '@assets/icon/filter.svg';
import certificateIcon from '@assets/icon/certificateIcon.svg';
import download from '@assets/icon/download.svg';
import checked from '@assets/icon/checkbox_checked.svg';
import unchecked from '@assets/icon/checkbox_unchecked.svg';
import checkedWhite from '@assets/icon/checkbox_checked_white.svg';
import uncheckedWhite from '@assets/icon/checkbox_unchecked_white.svg';
import playSonic from '@assets/icon/sonic_playing.svg';
import play from '@assets/icon/play.svg';
import wallet from '@assets/icon/wallet.svg';
import inMyWallet from '@assets/icon/in_my_wallet.svg';
import editIcon from '@assets/icon/edit.svg';
import musicIcon from '@assets/icon/music.svg';
import shieldLine from '@assets/icon/shield_line.svg';

import badge_1 from '@assets/home/badge_1.png';
import badge_2 from '@assets/home/badge_2.png';
import badge_3 from '@assets/home/badge_3.png';
import badge_4 from '@assets/home/badge_4.png';

import playBlue from '@assets/play_blue.svg';
import trashRed from '@assets/icon/trash_red.svg';
import trashWhite from '@assets/icon/trash_white.svg';
import nftIcon from '@assets/icon/nft.svg';
import webIcon from '@assets/icon/web.svg';
import twitterIcon from '@assets/icon/twitter.svg';
import instagramIcon from '@assets/icon/insta.svg';
import youtubeIcon from '@assets/icon/youtube.svg';
import goodduckIcon from '@assets/icon/logo_goodduck.svg';
import shareIcon from '@assets/icon/share.svg';
import calendar from '@assets/icon/calendar.svg';
import supply from '@assets/icon/supply.svg';

import award from '@assets/icon/award.svg';
import clock from '@assets/icon/clock.svg';

import playBlack from '@assets/icon/play_black.svg';
import pauseBlack from '@assets/icon/pause_black.svg';
import volume from '@assets/icon/volume.svg';
import volumeMute from '@assets/icon/mute.svg';
import successCheckBlack from '@assets/icon/success_circle_black.svg';
import warning from '@assets/icon/warning.svg';
import warningRounded from '@assets/icon/warningRound.svg';
import redX from '@assets/icon/redX.svg';
import report from '@assets/icon/report.svg';

import sun from '@assets/icon/sun.svg';
import moon from '@assets/icon/moon.svg';
import system from '@assets/icon/monitor_cog.svg';

import minus from '@assets/icon/minus-solid.svg';
import deleteUser from '@assets/icon/deleteuser.png';

import defaultAvatar from '@assets/default_avatar.webp';
import defaultArtistBgImage from '@assets/defaultArtistBg.png';
import barcodeImage from '@assets/barcode.png';
import defaultPlaylistCover from '@assets/playlist_placeholder.jpg';
import editPLFilled from '@assets/icon/edit_pencel_filled.svg';
import editPLEmpty from '@assets/icon/edit_pencel_empty.svg';
import upload from '@assets/icon/upload.svg';
import plus from '@assets/icon/plus.svg';
import inspection from '@assets/icon/inspection.svg';
import uploading from '@assets/icon/uploadingdots.svg';
import uploadFail from '@assets/icon/uploadfail.svg';
import bluecheck from '@assets/icon/bluecheck.svg';
import kebabWhite from '@assets/icon/kebab_white.svg';
import hamburgerWhite from '@assets/icon/hamburger_white.svg';
import prevPlay from '@assets/icon/prev_play.svg';
import nextPlay from '@assets/icon/next_play.svg';
import playFilled from '@assets/icon/play_filled.svg';

// ─────────────────────────────────────────────────────────────
// 아이콘 맵 & 타입
// ─────────────────────────────────────────────────────────────

/** 전체 아이콘 에셋 맵 */
export const Icons = {
  cart: cartIcon,
  cartWhite: cartIconWhite,
  user: userIcon,
  copy: copyIcon,
  check: checkIcon,
  checkRounded: checkIconRounded,
  errorRounded: errorIconRounded,
  close: closeIcon,
  search: searchIcon,
  search2: searchIcon2,
  google,
  kakao,
  hidePassword,
  showPassword,
  arrowRight,
  arrowLeft,
  arrowLeft2,
  arrowLeftWhite,
  arrowDown,
  badge_1,
  badge_2,
  badge_3,
  badge_4,
  filter,
  certificateIcon,
  download,
  checked,
  unchecked,
  checkedWhite,
  uncheckedWhite,
  playBlue,
  trashRed,
  trashWhite,
  playSonic,
  play,
  prevPlay,
  nextPlay,
  wallet,
  arrowRight12,
  inMyWallet,
  cartEmpty,
  editIcon,
  musicIcon,
  shieldLine,
  nft: nftIcon,
  web: webIcon,
  twitter: twitterIcon,
  instagram: instagramIcon,
  youtube: youtubeIcon,
  goodduck: goodduckIcon,
  share: shareIcon,
  calendar,
  supply,
  award,
  clock,
  playBlack,
  pauseBlack,
  playFilled,
  successCheckBlack,
  warning,
  warningRounded,
  redX,
  report,
  sun,
  moon,
  system,
  volume,
  volumeMute,
  minus,
  deleteUser,
  defaultAvatar,
  defaultArtistBgImage,
  barcode: barcodeImage,
  defaultPlaylistCover,
  editPLFilled,
  editPLEmpty,
  upload,
  plus,
  inspection,
  uploading,
  uploadFail,
  bluecheck,
  kebabWhite,
  hamburgerWhite,
} as const;

export type IconName = keyof typeof Icons;

// ─────────────────────────────────────────────────────────────
// Icon 컴포넌트
// ─────────────────────────────────────────────────────────────

interface IconProps {
  name: IconName;
  size?: number | string;
  color?: string;
  className?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
}

export const Icon: React.FC<IconProps> = ({
  name,
  size = 24,
  color,
  className,
  onClick,
  style,
}) => {
  const iconSrc = Icons[name];
  const theme = useStyledTheme();
  const isDarkMode = theme.colors.fontBlackText === '#FFFFFF';

  // 다크모드에서 밝게 반전할 아이콘 (원래 어두운 아이콘)
  const darkToLightIcons: IconName[] = [
    'cart', 'cartEmpty', 'user', 'copy', 'search', 'filter',
    'editIcon', 'musicIcon', 'arrowRight', 'arrowRight12',
    'certificateIcon', 'download', 'inMyWallet',
    'play', 'playBlue', 'checked', 'unchecked',
    'nft', 'web', 'twitter', 'instagram', 'youtube', 'goodduck', 'share',
    'calendar', 'supply', 'playBlack', 'pauseBlack', 'sun', 'moon', 'system', 'close',
  ];

  // 다크모드에서 어둡게 반전할 아이콘 (원래 흰색 아이콘)
  const lightToDarkIcons: IconName[] = [
    'cartWhite', 'inMyWallet', 'clock', 'download', 'wallet',
  ];

  // 색상 미지정 시 다크모드 자동 반전 여부 결정
  const shouldInvertToDark  = !color && isDarkMode && lightToDarkIcons.includes(name);
  const shouldInvertToLight = !color && isDarkMode && darkToLightIcons.includes(name);

  // upload 아이콘에 커스텀 색상 적용 시 인라인 SVG로 렌더링
  if (name === 'upload' && color) {
    return (
      <UploadIconWrapper
        $size={size}
        $color={color}
        className={className}
        onClick={onClick}
        $clickable={!!onClick}
        style={style}
      >
        <svg viewBox="0 0 180 180" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M27.84 101.28C27.84 109.26 30.6 116.16 36.18 121.86C41.76 127.62 48.54 130.68 56.58 131.04C57.24 131.04 57.6 130.68 57.6 130.02V122.04C57.6 121.32 57.24 120.9 56.58 120.9C51.42 120.66 47.1 118.62 43.5 114.78C39.9 110.94 38.1 106.44 38.1 101.28C38.1 96.3597 39.78 92.0397 43.14 88.3197C46.5 84.6597 50.7 82.4997 55.74 81.8997H58.92C59.7 81.8997 60.12 81.5397 60.12 80.8197L60.48 77.3997C61.14 70.9197 63.9 65.4597 68.76 61.0797C73.62 56.6997 79.38 54.4797 85.92 54.4797C92.4 54.4797 98.1 56.6997 103.02 61.0797C107.94 65.4597 110.7 70.9197 111.36 77.3997L111.84 80.8197C111.84 81.5397 112.2 81.8997 112.92 81.8997H122.58C127.92 81.8997 132.54 83.8197 136.44 87.6597C142.29 93.4197 142.32 96.0597 142.32 101.22C142.32 106.38 140.52 110.88 136.92 114.72C133.32 118.56 128.94 120.6 123.84 120.84C123.06 120.84 122.64 121.2 122.64 121.98V129.96C122.64 130.62 123.06 130.98 123.84 130.98C129.06 130.86 133.86 129.42 138.24 126.72C142.62 124.02 146.1 120.42 148.62 115.92C151.14 111.42 152.4 106.5 152.4 101.28C152.4 97.2597 151.62 93.4197 150.06 89.8197C148.573 86.2959 146.413 83.0961 143.7 80.3997C140.971 77.714 137.753 75.5755 134.22 74.0997C130.56 72.5397 126.72 71.7597 122.7 71.7597H120.78C118.925 63.9325 114.46 56.9702 108.12 52.0197C101.876 46.9726 94.0687 44.2571 86.04 44.3397C77.58 44.3397 70.02 46.9797 63.48 52.2597C56.94 57.5397 52.74 64.2597 50.88 72.4197C44.22 73.9797 38.76 77.3997 34.5 82.7997C29.94 88.2597 27.84 94.3797 27.84 101.28ZM69.48 105.06C69.48 106.56 69.96 107.82 70.92 108.9C71.82 109.8 73.02 110.28 74.58 110.28C76.02 110.28 77.28 109.8 78.3 108.9L84.96 102.06V125.94C84.96 127.38 85.44 128.58 86.34 129.6C87.3 130.62 88.44 131.1 89.88 131.1C91.26 131.1 92.46 130.62 93.48 129.6C94.5 128.58 94.98 127.38 94.98 125.94V102.3L101.7 108.96C104.1 110.82 106.56 110.82 109.02 108.96C109.98 108.06 110.46 106.8 110.46 105.24C110.46 103.8 109.98 102.6 109.02 101.52L93.48 86.0997C93.0131 85.6225 92.4518 85.248 91.8319 85C91.212 84.7521 90.5472 84.6362 89.88 84.6597C88.44 84.6597 87.24 85.1397 86.34 86.0997L70.86 101.52C69.96 102.48 69.48 103.62 69.48 105.06Z" fill="currentColor"/>
        </svg>
      </UploadIconWrapper>
    );
  }

  return (
    <StyledIcon
      src={iconSrc}
      alt={name}
      $size={size}
      $color={color}
      $shouldInvertToLight={shouldInvertToLight}
      $shouldInvertToDark={shouldInvertToDark}
      className={className}
      onClick={onClick}
      $clickable={!!onClick}
      style={style}
    />
  );
};

// ─────────────────────────────────────────────────────────────
// 내부 styled-components
// ─────────────────────────────────────────────────────────────

const UploadIconWrapper = styled.div<{
  $size: number | string;
  $color: string;
  $clickable?: boolean;
}>`
  width: ${({ $size }) => typeof $size === 'number' ? `${$size}px` : $size};
  height: ${({ $size }) => typeof $size === 'number' ? `${$size}px` : $size};
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $clickable }) => $clickable && 'cursor: pointer;'}
  transition: all 0.2s ease;

  svg {
    width: 100%;
    height: 100%;
  }

  &:hover {
    ${({ $clickable }) => $clickable && 'opacity: 0.8;'}
  }
`;

const StyledIcon = styled.img<{
  $size: number | string;
  $color?: string;
  $clickable?: boolean;
  $shouldInvertToLight?: boolean;
  $shouldInvertToDark?: boolean;
}>`
  width: ${({ $size }) => typeof $size === 'number' ? `${$size}px` : $size};
  height: ${({ $size }) => typeof $size === 'number' ? `${$size}px` : $size};
  display: block;
  ${({ $color }) => $color && `filter: ${getColorFilter($color)};`}
  ${({ $shouldInvertToLight }) => $shouldInvertToLight && 'filter: brightness(0) invert(1);'}
  ${({ $shouldInvertToDark }) => $shouldInvertToDark && 'filter: brightness(0);'}
  ${({ $clickable }) => $clickable && 'cursor: pointer;'}
  transition: all 0.2s ease;

  &:hover {
    ${({ $clickable }) => $clickable && 'opacity: 0.8;'}
  }
`;

// ─────────────────────────────────────────────────────────────
// 색상 필터 유틸리티
// ─────────────────────────────────────────────────────────────

/** 색상 이름 또는 hex → CSS filter 문자열 변환 */
function getColorFilter(color: string): string {
  switch (color) {
    case 'white':
      return 'brightness(0) invert(1)';
    case 'black':
      return 'brightness(0)';
    case 'primary':
      return 'brightness(0) saturate(100%) invert(51%) sepia(65%) saturate(1559%) hue-rotate(196deg) brightness(102%) contrast(101%)';
    case 'auto': // 테마에 따라 자동 변경
      return 'var(--icon-filter)';
    default:
      return color.startsWith('#') ? hexToFilter(color) : 'none';
  }
}

/** hex 색상 문자열을 CSS filter 값으로 변환 */
function hexToFilter(hex: string): string {
  hex = hex.replace('#', '');
  if (hex.length === 8) {
    hex = hex.substring(0, 6); // 알파 채널 제거
  }

  // hex → RGB
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return 'none';

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  // 밝기 계산 (0–1)
  const brightness = Math.sqrt(
    0.299 * (r / 255) ** 2 +
    0.587 * (g / 255) ** 2 +
    0.114 * (b / 255) ** 2,
  );

  // RGB → HSL 변환
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;

  let hue = 0;
  if (delta !== 0) {
    if (max === rNorm) {
      hue = ((gNorm - bNorm) / delta) % 6;
    } else if (max === gNorm) {
      hue = (bNorm - rNorm) / delta + 2;
    } else {
      hue = (rNorm - gNorm) / delta + 4;
    }
    hue = Math.round(hue * 60);
    if (hue < 0) hue += 360;
  }

  const lightness  = (max + min) / 2;
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));

  // CSS filter 생성
  const invert          = lightness > 0.5 ? Math.round(lightness * 100) : 0;
  const sepia           = Math.round(saturation * 100);
  const saturate        = Math.round((1 + saturation) * 100);
  const brightnessFilter = Math.round(brightness * 100 + 50);

  return `brightness(0) invert(${invert}%) sepia(${sepia}%) saturate(${saturate}%) hue-rotate(${hue}deg) brightness(${brightnessFilter}%)`;
}
